import { FormControl, InputLabel, Select as MuiSelect, SelectChangeEvent } from '@mui/material';
import React, { forwardRef, useId, useMemo } from 'react';
import { SxProps } from '../../utils';
import { MenuItem } from '../Menu';
import { SelectOption } from '../Select';
import { Stack } from '../Stack';

export interface SimpleSelectProps {
  /**
   * Callback fired when a menu item is selected.
   */
  onChange: (event: SelectChangeEvent<string>) => void;
  /**
   * The option elements to populate the select with.
   */
  options: SelectOption[] | string[];
  /**
   * The `input` value.
   */
  value: string;
  /**
   * If `true`, the component is disabled.
   * The prop defaults to the value (`false`) inherited from the parent FormControl component.
   */
  disabled?: boolean;
  /**
   * Controls setting the form control to full width
   */
  fullWidth?: boolean;
  /**
   * React component used as the icon before the text of the selected value
   */
  Icon?: React.ReactNode;
  /**
   * The label of the select
   */
  label?: string;
  /**
   * Renders the options in the dropdown and the selected value
   */
  renderOption?: (option: SelectOption) => React.ReactNode;
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps;
}

export const SimpleSelect = forwardRef<HTMLDivElement, SimpleSelectProps>(function SimpleSelect(
  { options: rawOptions, Icon, label, fullWidth, renderOption, ...props },
  ref
) {
  const labelId = useId();
  const options = useMemo(() => normalizeOptions(rawOptions), [rawOptions]);

  const select = (
    <MuiSelect
      {...props}
      label={label}
      ref={ref}
      labelId={label !== undefined ? labelId : undefined}
      renderValue={(value) => {
        const selectedOption = options.find((option) => option.key === value) ?? { label: value, key: value };
        return renderOption ? (
          renderOption(selectedOption)
        ) : (
          <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 1 }}>
            {Icon}
            {selectedOption.label}
          </Stack>
        );
      }}
    >
      {options.map((option) => (
        <MenuItem key={`${option.key}-${option.label}`} value={option.key} description={option.description}>
          {renderOption ? renderOption(option) : option.label}
        </MenuItem>
      ))}
    </MuiSelect>
  );

  if (label !== undefined) {
    return (
      <FormControl fullWidth={fullWidth}>
        <InputLabel id={labelId}>{label}</InputLabel>
        {select}
      </FormControl>
    );
  }

  return select;
});

/**
 * If options is a string-based array, convert it to an array of SelectOption objects.
 */
const normalizeOptions = (options: SelectOption[] | string[]): SelectOption[] => {
  return options.map((option) => {
    if (typeof option === 'string') {
      return { key: option, label: option };
    }
    return option;
  });
};
